import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Bell, CalendarPlus, CheckCircle2, ChevronRight, Clock, Loader2, MessageCircle } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import DateFieldPT from "@/components/DateFieldPT";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const onlyDigits = (s) => (s || "").toString().replace(/\D+/g, "");

// Valid validity presets understood by the budget form.
const VALIDITY_LADDER = [5, 15, 30];
const nextValidityStep = (current) => {
  const cur = Number(current) || 0;
  // Encontra o próximo degrau da escada que seja estritamente maior.
  return VALIDITY_LADDER.find((d) => d > cur) || null;
};

/**
 * ExpiredBudgetsAlert
 * -------------------
 * Admin dashboard widget listing budgets the cron just flipped to
 * `validade_expirada`. Actions per row:
 *   - Enviar lembrete (push + optional WhatsApp link)
 *   - OK, ciente (dismiss from panel)
 *   - Ver orçamento (link)
 */
export default function ExpiredBudgetsAlert() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Dialog para alterar a DATA DO ORÇAMENTO quando a validade já está em 30 dias
  // (próximo degrau da escada não existe → o admin prefere mover a data base).
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftBudget, setShiftBudget] = useState(null);
  const [shiftDate, setShiftDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/budgets/expired-pending-ack", { silent: true });
      setItems(r.data || []);
    } catch {
      // Silent fail — widget just doesn't render
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // Refresh every minute
    return () => clearInterval(t);
  }, []);

  // Extends validity_days in-place.  Backend auto-reverts the status and clears
  // the expiry flags when the new validity date is in the future → the row
  // disappears from this panel on next refresh.
  const extendValidity = async (b, targetDays) => {
    setBusyId(b.id);
    try {
      await api.put(`/budgets/${b.id}`, { validity_days: targetDays });
      toast.success(`Validade do Nº ${b.number} estendida para ${targetDays} dias`);
      setItems((arr) => arr.filter((x) => x.id !== b.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao estender validade");
    } finally {
      setBusyId(null);
    }
  };

  const openShiftDialog = (b) => {
    setShiftBudget(b);
    // Pré-preenche com a data atual do orçamento (o admin ajusta pra frente).
    setShiftDate(b.budget_date || "");
    setShiftOpen(true);
  };

  const confirmShift = async () => {
    if (!shiftBudget || !shiftDate) return;
    setBusyId(shiftBudget.id);
    try {
      await api.put(`/budgets/${shiftBudget.id}`, { budget_date: shiftDate });
      toast.success(`Data do Nº ${shiftBudget.number} atualizada para ${formatDateBR(shiftDate)}`);
      setItems((arr) => arr.filter((x) => x.id !== shiftBudget.id));
      setShiftOpen(false);
      setShiftBudget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar data");
    } finally {
      setBusyId(null);
    }
  };

  const acknowledge = async (b) => {
    setBusyId(b.id);
    try {
      await api.post(`/budgets/${b.id}/acknowledge-expiry`);
      toast.success(`Orçamento Nº ${b.number} marcado como ciente`);
      setItems((arr) => arr.filter((x) => x.id !== b.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    } finally {
      setBusyId(null);
    }
  };

  const acknowledgeAll = async () => {
    if (!window.confirm(`Marcar TODOS os ${items.length} orçamentos vencidos como ciente?`)) return;
    setBusyId("__all__");
    try {
      const r = await api.post(`/budgets/expired-ack-all`);
      toast.success(`${r.data?.updated || 0} orçamentos marcados como ciente`);
      setItems([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar todos");
    } finally {
      setBusyId(null);
    }
  };

  const resendNotice = async (b) => {
    setBusyId(b.id);
    try {
      await api.post(`/budgets/${b.id}/resend-expiry-notice`);
      toast.success("Aviso reenviado para a escola");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reenviar aviso");
    } finally {
      setBusyId(null);
    }
  };

  const whatsappLink = (b) => {
    const phone = onlyDigits(b.director_phone);
    if (!phone) return null;
    const name = b.director_name || "Diretor(a)";
    const school = b.school_nickname || b.school_name || "escola";
    const msg = encodeURIComponent(
      `Olá ${name}! O orçamento Nº ${b.number} da ${school} teve a validade expirada em ${formatDateBR(b.validity_date)}. Gostaria de reativá-lo? — AMC Escolas`,
    );
    return `https://wa.me/55${phone}?text=${msg}`;
  };

  if (!loading && items.length === 0) return null;

  return (
    <div
      data-testid="expired-budgets-alert"
      className="bg-amber-50 border-2 border-amber-200 rounded-2xl card-shadow p-4"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-amber-200 text-amber-700 flex items-center justify-center shrink-0">
          <AlertCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-black text-amber-900 leading-tight">
            {items.length === 1
              ? "1 orçamento acabou de expirar"
              : `${items.length} orçamentos acabaram de expirar`}
          </h3>
          <p className="text-[11px] text-amber-800">
            Reenvie um aviso para a escola ou marque como ciente para remover da lista.
          </p>
        </div>
        {items.length > 1 && (
          <button
            type="button"
            onClick={acknowledgeAll}
            disabled={busyId === "__all__"}
            className="shrink-0 inline-flex items-center gap-1 bg-amber-900 hover:bg-black text-white text-[10px] font-semibold rounded-full px-2.5 h-7 shadow-sm disabled:opacity-50"
            data-testid="btn-expired-ack-all"
            title="Marcar todos como ciente"
          >
            {busyId === "__all__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Ciente de todos
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-amber-800 flex items-center gap-1.5 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando...
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => {
            const isBusy = busyId === b.id;
            const wa = whatsappLink(b);
            return (
              <div
                key={b.id}
                data-testid={`expired-row-${b.id}`}
                className="relative bg-white rounded-xl border border-amber-200 p-3"
              >
                {isBusy && (
                  <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center z-10">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-[#1e3a8a] uppercase tracking-wide truncate">
                      Nº {b.number} · {b.school_nickname || b.school_name || "—"}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Validade: <strong>{formatDateBR(b.validity_date)}</strong>
                      {" · "}
                      <strong>{BRL(b.total || 0)}</strong>
                    </p>
                  </div>
                  <Link
                    to={`/orcamentos/${b.id}`}
                    className="shrink-0 inline-flex items-center text-[11px] text-[#7c3aed] font-semibold hover:underline"
                    data-testid={`btn-expired-view-${b.id}`}
                  >
                    Abrir <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {(() => {
                    const nextStep = nextValidityStep(b.validity_days);
                    if (nextStep) {
                      return (
                        <button
                          type="button"
                          onClick={() => extendValidity(b, nextStep)}
                          className="inline-flex items-center gap-1 bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[11px] font-semibold rounded-full px-2.5 h-7 shadow-sm"
                          data-testid={`btn-expired-extend-${b.id}`}
                          title={`Estender a validade para ${nextStep} dias e reativar o orçamento`}
                        >
                          <Clock className="w-3 h-3" /> Estender {nextStep}d
                        </button>
                      );
                    }
                    // Já está em 30 dias — próximo passo é mover a data do orçamento
                    return (
                      <button
                        type="button"
                        onClick={() => openShiftDialog(b)}
                        className="inline-flex items-center gap-1 bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[11px] font-semibold rounded-full px-2.5 h-7 shadow-sm"
                        data-testid={`btn-expired-shift-${b.id}`}
                        title="Alterar a data do orçamento (validade já está em 30 dias)"
                      >
                        <CalendarPlus className="w-3 h-3" /> Mudar data
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => resendNotice(b)}
                    className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold rounded-full px-2.5 h-7 shadow-sm"
                    data-testid={`btn-expired-notify-${b.id}`}
                    title="Enviar notificação no app para a escola"
                  >
                    <Bell className="w-3 h-3" /> Enviar aviso
                  </button>
                  {wa && (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-full px-2.5 h-7 shadow-sm"
                      data-testid={`btn-expired-wa-${b.id}`}
                      title="Enviar WhatsApp para o diretor"
                    >
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => acknowledge(b)}
                    className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px] font-semibold rounded-full px-2.5 h-7"
                    data-testid={`btn-expired-ack-${b.id}`}
                    title="Já estou ciente — remover do painel"
                  >
                    <CheckCircle2 className="w-3 h-3" /> OK, ciente
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Dialog: alterar DATA DO ORÇAMENTO quando validade já = 30 dias */}
      <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Alterar data do orçamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              A validade já está em 30 dias (máximo). Mover a data do orçamento
              para frente também recalcula a validade e reativa o orçamento.
            </p>
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase">
                Nova data do orçamento
              </label>
              <DateFieldPT
                value={shiftDate}
                onChange={setShiftDate}
                testid="shift-budget-date"
                placeholder="Selecione a nova data"
                fromYear={2026}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShiftOpen(false)}
              className="px-3 h-9 rounded-md bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700"
              data-testid="btn-shift-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmShift}
              disabled={!shiftDate || busyId === shiftBudget?.id}
              className="px-3 h-9 rounded-md bg-[#7c3aed] hover:bg-[#6d28d9] text-sm font-semibold text-white disabled:opacity-50"
              data-testid="btn-shift-confirm"
            >
              Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
